import React, { useState, useRef, useCallback, useEffect } from "react";
import { StandaloneSearchBox } from "@react-google-maps/api";

const GoogleAutocomplete = ({
  onSelect,
  placeholder = "Enter your address...",
  name,
  // When true (default), reveal a manual, required postal-code input if the
  // selected Google place has no postal_code component — Google occasionally
  // omits it, which would otherwise dead-end the user behind a "postal code
  // required" validation with no field to type it into.
  requirePostalCode = true,
  postalCodeLabel = "Postal Code",
  postalCodePlaceholder = "Enter postal code",
  // Optional error string shown under the manual postal-code input.
  postalCodeError,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [needPostalCode, setNeedPostalCode] = useState(false);
  const [postalCode, setPostalCode] = useState("");
  const searchBoxRef = useRef(null);
  // Holds the extracted address from the last place selection so the manual
  // postal-code value can be merged into it without losing line1/city/state.
  const lastExtractedRef = useRef(null);

  // Parents pass a fresh inline onSelect every render; keep the latest so the
  // manual postal-code handler never fires a stale closure.
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  const handleLoad = useCallback((ref) => {
    searchBoxRef.current = ref;
  }, []);

  const handlePlaceChanged = useCallback(() => {
    if (!searchBoxRef.current) return;

    const places = searchBoxRef.current.getPlaces();
    if (!places || places.length === 0) return;

    const place = places[0];
    const address = place.formatted_address;
    setInputValue(address);

    const addressComponents = place.address_components || [];

    const getComponent = (types) =>
      addressComponents.find((comp) => types.some((type) => comp.types.includes(type)));

    const extractedAddress = {
      addressLine1:
        `${getComponent(["street_number"])?.long_name ?? ""} ${getComponent(["route"])?.long_name ?? ""}`.trim() ||
        getComponent(["premise"])?.long_name ||
        getComponent(["sublocality_level_1"])?.long_name ||
        // Google didn't parse a street line (e.g. a city-level pick) — fall back
        // to the address the user actually selected so the visible text is what
        // gets captured/validated, instead of being treated as empty.
        address ||
        "",
      city: getComponent(["locality"])?.long_name || getComponent(["administrative_area_level_3"])?.long_name,
      state: getComponent(["administrative_area_level_1"])?.short_name,
      postalCode: getComponent(["postal_code"])?.long_name,
    };
    if (!extractedAddress.city) {
      extractedAddress.city = address?.split(",")[1]?.trim() || "";
    }

    lastExtractedRef.current = extractedAddress;

    // Reveal the manual postal-code field only when Google didn't give one.
    if (requirePostalCode && !extractedAddress.postalCode) {
      setNeedPostalCode(true);
      setPostalCode("");
    } else {
      setNeedPostalCode(false);
    }

    if (onSelectRef.current) onSelectRef.current({ ...place, extractedAddress });
  }, [requirePostalCode]);

  const handlePostalCodeChange = (e) => {
    const value = e.target.value.replace(/[^A-Za-z0-9 -]/g, "").slice(0, 10);
    setPostalCode(value);
    // Re-emit the selected address with the manually entered postal code so the
    // parent's existing onSelect mapping (extractedAddress.postalCode -> its
    // postal field) just works.
    const merged = { ...(lastExtractedRef.current || {}), postalCode: value };
    if (onSelectRef.current) onSelectRef.current({ extractedAddress: merged });
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value === "") {
      setNeedPostalCode(false);
      setPostalCode("");
      lastExtractedRef.current = null;
      if (onSelectRef.current) onSelectRef.current(null);
    }
  };

  return (
    <>
      <StandaloneSearchBox onLoad={handleLoad} onPlacesChanged={handlePlaceChanged}>
        <input
          name={name}
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleChange}
        />
      </StandaloneSearchBox>
      {needPostalCode && (
        <div className="form-group mt-2 mb-0">
          <label>{postalCodeLabel}*</label>
          <input
            type="text"
            className="form-control"
            placeholder={postalCodePlaceholder}
            value={postalCode}
            onChange={handlePostalCodeChange}
            autoFocus
          />
          {postalCodeError && <p className="text-danger mb-0">{postalCodeError}</p>}
        </div>
      )}
    </>
  );
};

export default GoogleAutocomplete;
